import { Link } from '@tanstack/react-router';

import type { Booking, BusinessDetail } from '../api/schemas';
import { bookingFacts, bookingTitle } from '../lib/bookingPresentation';
import { formatTime } from '../lib/format';
import { reservationsSummary, startsIn } from '../lib/reservations-summary';
import '../styles/reservations-summary.css';

type ReservationsSummaryProps = {
  bookings: readonly Booking[];
  now: Date;
  business: BusinessDetail | undefined;
};

function plural(count: number): string {
  return count === 1 ? '1 reserva' : `${count} reservas`;
}

/**
 * The top of Reservas: who is next, big enough to read across the counter, and
 * how the day and the week look. The next visit is the link to its detail.
 */
export function ReservationsSummary({ bookings, now, business }: ReservationsSummaryProps) {
  const summary = reservationsSummary(bookings, now);
  const next = summary.next;
  return (
    <section className="res-summary" aria-label="Resumen de reservas">
      {next ? (
        <Link
          to="/reservas/$bookingId"
          params={{ bookingId: next.booking.id }}
          className="res-summary-next"
          aria-label={`${next.ongoing ? 'Ahora' : 'Próxima reserva'}: ${formatTime(next.booking.startsAt)}, ${bookingTitle(next.booking, business)}`}
        >
          <span className="res-summary-eyebrow">
            {next.ongoing ? 'Ahora' : 'Próxima reserva'}
            {next.ongoing ? null : <span className="res-summary-when"> · {startsIn(new Date(next.booking.startsAt), now)}</span>}
          </span>
          <span className="res-summary-time">{formatTime(next.booking.startsAt)}</span>
          <span className="res-summary-who">{bookingTitle(next.booking, business)}</span>
          <span className="res-summary-facts">{bookingFacts(next.booking, business).join(' · ')}</span>
        </Link>
      ) : (
        <div className="res-summary-next res-summary-next-empty">
          <span className="res-summary-eyebrow">Próxima reserva</span>
          <span className="res-summary-who">Nada en la agenda todavía.</span>
          <span className="res-summary-facts">Cuando alguien reserve en la app o con tu enlace, aparece aquí.</span>
        </div>
      )}
      <dl className="res-summary-counts">
        <div>
          <dt>Hoy</dt>
          <dd aria-label={plural(summary.today)}>{summary.today}</dd>
        </div>
        <div>
          <dt>Mañana</dt>
          <dd aria-label={plural(summary.tomorrow)}>{summary.tomorrow}</dd>
        </div>
        <div>
          <dt>7 días</dt>
          <dd aria-label={plural(summary.week)}>{summary.week}</dd>
        </div>
      </dl>
    </section>
  );
}
